/**
 * EventAdapter - Maps legacy event handlers to TanStack Table equivalents
 * Maintains backward compatibility with existing event handling patterns
 */
import { Row, ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { LegacyFilter } from "./ColumnAdapter";
import { StateAdapter } from "./StateAdapter";

export class EventAdapter {
  /**
   * Maps legacy row selection handler to TanStack row click
   */
  static mapRowSelectionHandler(
    legacyHandler: (row: any, getOption?: Api.OptionsGrud) => void,
    getOption?: Api.OptionsGrud
  ) {
    return (row: Row<any>) => {
      if (legacyHandler) {
        legacyHandler(row.original, getOption);
      }
    };
  }

  /**
   * Maps legacy filter change handler to TanStack filter change
   */
  static mapFilterChangeHandler(
    legacyHandler?: (changedFilter: LegacyFilter[]) => void,
    resetPagination?: () => void
  ) {
    return (newFilters: ColumnFiltersState) => {
      // Reset pagination when filters change
      if (resetPagination) {
        resetPagination();
      }

      // Call legacy handler with converted filters
      if (legacyHandler) {
        const legacyFilters = StateAdapter.mapFiltersFromTanStack(newFilters);
        legacyHandler(legacyFilters);
      }
    };
  }

  /**
   * Maps legacy page change handler to TanStack pagination change
   */
  static mapPageChangeHandler(
    legacyHandler?: (page: number) => void
  ) {
    return (newPagination: PaginationState) => {
      if (legacyHandler) {
        legacyHandler(newPagination.pageIndex);
      }
    };
  }

  /**
   * Creates row click handler with proper event handling
   */
  static createRowClickHandler(
    rowSelectionHandler: (row: any, getOption?: Api.OptionsGrud) => void,
    getOption?: Api.OptionsGrud
  ) {
    return (event: React.MouseEvent, row: Row<any>) => {
      // Prevent default behavior and stop propagation like original
      event.preventDefault();
      event.stopPropagation();

      // Call the mapped handler
      const mappedHandler = this.mapRowSelectionHandler(rowSelectionHandler, getOption);
      mappedHandler(row);
    };
  }

  /**
   * Creates header click handler for sorting
   */
  static createHeaderClickHandler(toggleSorting: () => void) {
    return (event: React.MouseEvent) => {
      event.preventDefault();
      toggleSorting();
    };
  }

  /**
   * Creates filter input handler
   */
  static createFilterInputHandler(setFilterValue: (value: any) => void) {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilterValue(event.target.value);
    };
  }

  /**
   * Creates dropdown filter handler
   */
  static createDropdownFilterHandler(setFilterValue: (value: any) => void) {
    return (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value;
      setFilterValue(value === "all" ? undefined : value);
    };
  }

  /**
   * Handles pagination button clicks
   */
  static createPaginationHandlers(table: any) {
    return {
      goToFirstPage: () => table.setPageIndex(0),
      goToPreviousPage: () => table.previousPage(),
      goToNextPage: () => table.nextPage(),
      goToLastPage: () => table.setPageIndex(table.getPageCount() - 1),
      goToPage: (page: number) => table.setPageIndex(page - 1), // Convert to 0-based
    };
  }

  /**
   * Handles input field page navigation
   */
  static createPageInputHandlers(table: any) {
    return {
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        const page = event.target.value ? Number(event.target.value) : 1;
        return page;
      },
      onKeyPress: (event: React.KeyboardEvent<HTMLInputElement>, page: number) => {
        if (event.key === "Enter") {
          const validPage = Math.max(1, Math.min(table.getPageCount(), page));
          table.setPageIndex(validPage - 1); // Convert to 0-based
        }
      },
      onBlur: (page: number) => {
        const validPage = Math.max(1, Math.min(table.getPageCount(), page));
        table.setPageIndex(validPage - 1); // Convert to 0-based
      },
    };
  }

  /**
   * Determines if row is selected (for styling)
   */
  static createRowSelectedChecker(
    isRowSelected: (row: any) => boolean
  ) {
    return (row: Row<any>) => isRowSelected(row.original);
  }

  /**
   * Creates TD props for row interaction (legacy compatibility)
   */
  static createTdProps(
    rowClickHandler: (event: React.MouseEvent, row: Row<any>) => void,
    isHidingTextActive: boolean
  ) {
    return (row: Row<any>) => ({
      onClick: (event: React.MouseEvent) => rowClickHandler(event, row),
      style: {
        cursor: "pointer",
        whiteSpace: isHidingTextActive ? "normal" : "pre-wrap",
      } as React.CSSProperties,
    });
  }

  /**
   * Creates TR props for row styling (legacy compatibility)
   */
  static createTrProps(
    isRowSelectedChecker: (row: Row<any>) => boolean
  ) {
    return (row: Row<any>) => ({
      className: isRowSelectedChecker(row) ? "selected-row" : "",
    });
  }
}